import type { DecisionSnapshot } from '../types/snapshot'

/**
 * Server-sent events over fetch, because EventSource cannot carry the bearer header.
 * Parses the wire format the API writes: `event: snapshot`, `id: <sequence>`, `data: <json>`,
 * blank line; comment lines (`: keepalive`) are ignored.
 */
export function parseEvents(chunk: string): { events: SseEvent[]; rest: string } {
  const events: SseEvent[] = []
  let rest = chunk
  for (;;) {
    const end = rest.indexOf('\n\n')
    if (end < 0) break
    const block = rest.slice(0, end)
    rest = rest.slice(end + 2)
    const event: SseEvent = { event: 'message', data: '' }
    const data: string[] = []
    for (const line of block.split('\n')) {
      if (line.startsWith(':')) continue
      const colon = line.indexOf(':')
      const field = colon < 0 ? line : line.slice(0, colon)
      const value = colon < 0 ? '' : line.slice(colon + 1).replace(/^ /, '')
      if (field === 'event') event.event = value
      else if (field === 'data') data.push(value)
      else if (field === 'id') event.id = value
    }
    event.data = data.join('\n')
    if (event.data.length > 0 || event.event !== 'message') events.push(event)
  }
  return { events, rest }
}

export interface SseEvent {
  event: string
  data: string
  id?: string
}

/**
 * Reads snapshots until the signal aborts. Reconnects on its own after a dropped stream,
 * with a short backoff — the daemon's heartbeat makes a missed event harmless, and the ticket
 * shows Checking… the moment a snapshot goes stale anyway.
 */
export async function readSnapshots(
  open: (signal: AbortSignal) => Promise<Response>,
  onSnapshot: (snapshot: DecisionSnapshot) => void,
  onStatus: (status: 'connected' | 'reconnecting' | 'denied') => void,
  signal: AbortSignal,
): Promise<void> {
  let backoff = 500
  while (!signal.aborted) {
    // A refusal we keep retrying past, so the status stays on screen instead of flickering.
    let refused = false
    try {
      const response = await open(signal)
      if (response.status === 401) {
        // The session, not the account. Nothing here recovers that; the trader signs in again.
        onStatus('denied')
        return
      }
      if (response.status === 404) {
        // The API says "not one of your accounts, OR the guardian has not published it yet",
        // and the second half resolves itself: a newly guarded account is live within a
        // minute, and the daemon republishes every snapshot after a restart. Giving up here
        // left the trader looking at a permanent refusal that a reload would have cleared.
        onStatus('denied')
        refused = true
      } else if (!response.ok || !response.body) {
        throw new Error(`stream ${response.status}`)
      } else {
        onStatus('connected')
        backoff = 500
        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        for (;;) {
          const { value, done } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          const parsed = parseEvents(buffer)
          buffer = parsed.rest
          for (const event of parsed.events) {
            if (event.event === 'snapshot') onSnapshot(JSON.parse(event.data) as DecisionSnapshot)
          }
        }
      }
    } catch (error) {
      if (signal.aborted) return
      void error
    }
    if (signal.aborted) return
    if (!refused) onStatus('reconnecting')
    await new Promise((resolve) => setTimeout(resolve, backoff))
    backoff = Math.min(backoff * 2, 5_000)
  }
}
